import React from 'react';
import { colors, type } from './theme';
import ThemeToggle from './ThemeToggle';
import useMediaQuery from './useMediaQuery';

// ── shona-tts page navbar ────────────────────────────────────────────────────
// Brand hard left, account controls hard right. Named ShonaNavbar so it can't
// be confused with src/Navbar.js, which belongs to the rest of the app and has
// nothing to do with these pages.
//
// Replaces the "Dzose" eyebrow that used to sit inside each page's centred
// header, plus the right-aligned auth row above it — the two were separate
// elements doing one job between them.
//
// `auth` is optional: pages without a Cognito provider (e.g. /shona-asr) render
// the brand alone rather than an empty right-hand side.
//
// `menuItems` is optional too: page-specific entries that sit beside the brand,
// each { id, label, onClick, hasPopup }. They are buttons, not links — they
// open something on the current page (e.g. the reader's "Who uses it?"
// dialog) rather than navigating away.
//
// Deliberately NOT sticky. These pages were just reordered so the input sits
// above the fold, and a persistent bar would take that space back on mobile.

// Same 560px line A11yStyles uses to start shrinking the navbar email.
const NARROW_QUERY = '(max-width: 560px)';

// Hover lives in CSS rather than onMouseEnter/onMouseLeave for the reason
// ThemeToggle documents: inline styles re-assigned from a handler don't
// reliably re-resolve their var() when the theme switches underneath them.
const CSS = `
.sn-nav-item {
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  min-height: 36px;
  padding: 0 12px;
  border-radius: 999px;
  border: 1px solid transparent;
  background: transparent;
  color: var(--sn-textDim);
  font: inherit;
  font-size: 13px;
  font-weight: 600;
  letter-spacing: 0.2px;
  white-space: nowrap;
  cursor: pointer;
  transition: color 0.15s, background 0.15s;
}
.sn-nav-item:hover {
  color: var(--sn-accent);
  background: var(--sn-accentBg);
}
`;

// `placeholder` keeps the button's size and place but draws nothing;
// visibility: hidden also takes it out of the tab order and the accessibility
// tree.
const SignInButton = ({ onClick, placeholder = false }) => (
  <button
    type="button"
    onClick={onClick}
    style={{
      flexShrink: 0,
      background: 'transparent',
      border: `1px solid ${colors.accentDim}`,
      color: colors.accent,
      borderRadius: 999,
      padding: '8px 16px',
      fontSize: 13,
      fontWeight: 600,
      letterSpacing: 0.2,
      cursor: 'pointer',
      visibility: placeholder ? 'hidden' : undefined,
    }}
  >
    Sign in
  </button>
);

const AccountControls = ({ auth, onSignIn, onSignOut, showEmail = true }) => {
  // While the session resolves, an invisible Sign in button holds the place
  // (not a spinner, so the bar doesn't flicker between states on load). It
  // has to take up room: on a phone the button only fits on a second row, and
  // adding that row once auth resolved pushed the whole page down.
  if (auth.isLoading) return <SignInButton placeholder />;

  if (auth.isAuthenticated) {
    const email = auth.user?.profile?.email ?? 'Signed in';
    return (
      <div
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 10,
          minWidth: 0,
          fontSize: 13,
          color: colors.textDim,
        }}
      >
        {showEmail && (
          <span
            className="shona-nav-email"
            title={email}
            style={{
              minWidth: 0,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {email}
          </span>
        )}
        <button
          type="button"
          onClick={onSignOut}
          title={showEmail ? undefined : `Signed in as ${email}`}
          style={{
            flexShrink: 0,
            background: 'transparent',
            border: `1px solid ${colors.border}`,
            color: colors.textDim,
            borderRadius: 999,
            padding: '7px 14px',
            fontSize: 13,
            fontWeight: 600,
            letterSpacing: 0.2,
            cursor: 'pointer',
          }}
        >
          Sign out
        </button>
      </div>
    );
  }

  return <SignInButton onClick={onSignIn} />;
};

const ShonaNavbar = ({
  auth,
  onSignIn,
  onSignOut,
  maxWidth = 960,
  menuItems = [],
}) => {
  const isNarrow = useMediaQuery(NARROW_QUERY);
  // Menu entries take the room the theme label and the email would use, so on
  // a phone those collapse to the toggle's icon and the Sign out button (whose
  // title still carries the address). Pages without entries are unaffected.
  const crowded = menuItems.length > 0 && isNarrow;

  return (
    <header
      style={{
        borderBottom: `1px solid ${colors.border}`,
        background: colors.bg,
      }}
    >
      <style>{CSS}</style>
      <nav
        aria-label="Site"
        style={{
          maxWidth,
          margin: '0 auto',
          // matches the horizontal padding of each page's <main>
          padding: '14px clamp(16px, 5vw, 24px)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          // last resort for the narrowest phones: the right-hand group drops
          // to its own row instead of pushing the bar wider than the screen
          flexWrap: 'wrap',
          columnGap: 16,
          rowGap: 10,
          // reserves the row height so the layout doesn't jump once auth resolves
          minHeight: 44,
        }}
      >
        <div
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 8,
            minWidth: 0,
          }}
        >
          <span
            style={{
              ...type.eyebrow,
              color: colors.accent,
              flexShrink: 0,
            }}
          >
            Dzose
          </span>
          {menuItems.map(item => (
            <button
              key={item.id}
              type="button"
              className="sn-nav-item"
              data-testid={`shona-nav-${item.id}`}
              aria-haspopup={item.hasPopup}
              onClick={item.onClick}
            >
              {item.label}
            </button>
          ))}
        </div>
        {/* Theme switch sits with the account controls, not beside the brand:
            both are page-level settings, and grouping them keeps the right-hand
            side the single place anything about "you" lives. */}
        <div
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 10,
            flexShrink: 0,
            marginLeft: 'auto',
          }}
        >
          <ThemeToggle compact={crowded} />
          {auth ? (
            <AccountControls
              auth={auth}
              onSignIn={onSignIn}
              onSignOut={onSignOut}
              showEmail={!crowded}
            />
          ) : null}
        </div>
      </nav>
    </header>
  );
};

export default ShonaNavbar;
